import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Center,
  Select,
  Pagination,
  Checkbox,
  Tabs,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
} from "@tabler/icons-react";
import classes from "./Staff.module.css";
import DeleteStaff from "./DeleteStaff";
import { RowDataStaff, AddStaffKPI, RowDataKPI } from "./types";
import { props } from "./props";
import { CustomTable } from "../Table/CustomTable";
import { kpi, staff } from "../types/RowDataTypes";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { useLocation, useNavigate } from "react-router-dom";
import { showErrorAlert } from "../../helper/alerts";
import Header from "../static/Header";
import GlobalContext from "../../api/GlobalProvider";
import getKPITAble, { KPITypes } from "../../DataDisplay/DataDisplayTable/KPITable";
import CurrentTab, { colFilterCurrent, colFiltersCurrent } from "../../DataDisplay/DataDisplayTab/SelectedStaffTab/Tabs/CurrentTab";
import SelectedStaffDisplayTab from "../../DataDisplay/DataDisplayTab/SelectedStaffTab/SelectedStaffDisplayTab";
import PassTab from "../../DataDisplay/DataDisplayTab/SelectedStaffTab/Tabs/PassTab";
import TabI from "../../DataDisplay/DataDisplayTab/TabI";

function Th({ children, sortable, reversed, sorted, onSort }: props) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilterCurrent.TITLE.toLowerCase(),
    colFilterCurrent.PERIOD.toLowerCase(),
    colFilterCurrent.DURATION.toLowerCase(),
    colFilterCurrent.SCORE.toLowerCase(),
    colFilterCurrent.TARGET.toLowerCase(),
  ];

  const isCurrent =
    window.location.href.split("/")[5] === "current" ? true : false;
//   console.log("the children");
//   console.log(children);
  return children!! === "action" && !isCurrent ? (
    <></>
  ) : (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <div className={classes.sortableHeader}>
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </div>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataStaff[] = [
  {
    staffId: "",
    name: "",
    phone_number: "",
    email: "",
    KPI: "",
    password: "",
    reenterPassword: "",
    action: "",
  },
];

export function SelectedStaff() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState<string>("");
  const [sortedData, setSortedData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataStaff | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [tableStateChanged, setTableStateChanged] = useState(0);
  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  //For Searching
  const colHeaders = ["ID", "Title", "Period", "Duration", "Score", "Target"];
  const colFilters = ["", "title", "period"];
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const resetPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  };

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFiltersCurrent[colFilterCurrent.TITLE],
    colFiltersCurrent[colFilterCurrent.PERIOD],
  ];

  for (const [key, value] of Object.entries(colFiltersCurrent)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const location = useLocation();
  const staffname = location.pathname.split("/")[2];
  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    kpi,
    getKPITAble().create(),
    {
      uid: location.pathname.split("/")[2],
      current: location.pathname.split("/")[3] ?? "current",
    }
  );

  const itemsOnPage = useRef(0);
  const totalRows = fetchedData.totalRows;
  const rowData: RowDataKPI[] | null = fetchedData.data as RowDataKPI[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataKpi, setRowDataKPI] = useState<RowDataKPI[] | null>(null);

  useEffect(() => {
    setRowDataKPI(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
  }, [rowData]);

  const [isDeleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [rowToDelete, setRowToDelete] = useState<RowDataKPI | null>(null);

  const handleDelete = (row: RowDataKPI) => {
    setRowToDelete(row);
    setDeleteConfirmationVisible(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete) {
      const uid = location.pathname.split("/")[2];

      axiosPrivate
        .post("/api/admin/kpi/delete", {
          uid: uid,
          id: rowToDelete.kpiObjectiveID,
        })
        .then((response) => {
          setDeleteConfirmationVisible(false);
          if (
            itemsOnPage.current == 1 &&
            currentPage !== 1 &&
            currentPage == Math.ceil(totalRows / itemsPerPage)
          )
            setCurrentPage((prev) => prev - 1);
          else changeTableState();
        })
        .catch((err) => {
          showErrorAlert("An error has occured");
        });
      setDeleteConfirmationVisible(false);
    }
  };

  const handleCancelDelete = () => {
    setRowToDelete(null);
    setDeleteConfirmationVisible(false);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  const isCurrent =
    location.pathname.split("/")[3] === "current" ? true : false;
  const rowsToMap = rowDataKpi ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.kpiObjectiveID}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.title}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{`${row.timePeriod
        .toString()
        .charAt(0)
        .toUpperCase()}${row.timePeriod.toString().slice(1)}`}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.duration}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.score}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.target}</Table.Td>
      {isCurrent && (
        <Table.Td className={classes.actionButtons} style={{ width: "20%" }}>
          <button
            onClick={() => handleDelete(row)}
            className={classes.trashbutton}
          >
            <IconTrash /> Delete
          </button>
        </Table.Td>
      )}
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  const nav = useNavigate();

  const handleTabChange = (val: string | null) => {
    val = val ?? "current";
    const pathNames = location.pathname.split("/");
    nav(`/staff/${pathNames[2]}/${val}`);
    if (currentPage !== 1)
      setCurrentPage(1);
    else
      changeTableState()
  };

  const handleSorting = (val: any) => {
    setReverseSortDirection(!reverseSortDirection);
    setSortBy(val);
    changeTableState();
    // console.log("sorting");
    // console.log(val);
    // console.log(reverseSortDirection);
  };

  const { staffName } = useContext(GlobalContext);
  const displayTab: SelectedStaffDisplayTab = new SelectedStaffDisplayTab(reverseSortDirection, sortBy, handleSorting, classes, Th, rowData, rows)
  const tabs: KPITypes[] = [KPITypes.CURRENT, KPITypes.HISTORY]

  return (
    <div className={classes.staffContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header
          title={
            <div>
              <span>[</span>
              <span className={classes.staffname}>{`${staffName}`}</span>
              <span>]'s KPI</span>
            </div>
          }
        />

        <div className={classes.searchBarContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
        </div>

        <div className={classes.tabsContainer}>
          <Tabs
            defaultValue="current"
            color="blueviolet"
            variant="pills"
            onChange={(value) => {
              handleTabChange(value);
            }}
          >
            <Tabs.List className={classes.tabHeader}>
              <Tabs.Tab value={KPITypes.CURRENT} className={classes.tabTitle}>
                Current
              </Tabs.Tab>
              <Tabs.Tab value={KPITypes.HISTORY} className={classes.tabTitle}>
                History
              </Tabs.Tab>
            </Tabs.List>

            {
              tabs.map(tab => {
                return displayTab.createTab(tab)
              })
            }
          </Tabs>
        </div>

        <div className={classes.footerContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` result`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>

        {/* Modal Form for Delete */}

        {/* should change to delete kpi */}
        {isDeleteConfirmationVisible && (
          <DeleteStaff
            isVisible={isDeleteConfirmationVisible}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
          />
        )}
      </ScrollArea>
    </div>
  );
}

export default SelectedStaff;
